import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const IntegrationStatusPanel = ({ onSyncNow, onConfigureIntegration }) => {
  const [integrations, setIntegrations] = useState([]);
  const [lastSyncTime, setLastSyncTime] = useState(new Date(Date.now() - 15 * 60 * 1000));

  useEffect(() => {
    const mockIntegrations = [
      {
        id: 'ad',
        name: 'Active Directory',
        type: 'LDAP',
        status: 'connected',
        lastSync: new Date(Date.now() - 15 * 60 * 1000),
        syncedUsers: 245,
        errors: 0,
        description: 'Primary user directory and authentication source'
      },
      {
        id: 'sso',
        name: 'Single Sign-On',
        type: 'SAML',
        status: 'connected',
        lastSync: new Date(Date.now() - 30 * 60 * 1000),
        syncedUsers: 198,
        errors: 0,
        description: 'SAML-based single sign-on provider'
      },
      {
        id: 'hr',
        name: 'HR System',
        type: 'API',
        status: 'warning',
        lastSync: new Date(Date.now() - 2 * 60 * 60 * 1000),
        syncedUsers: 180,
        errors: 3,
        description: 'Employee data and organizational structure'
      },
      {
        id: 'email',
        name: 'Email System',
        type: 'Exchange',
        status: 'connected',
        lastSync: new Date(Date.now() - 45 * 60 * 1000),
        syncedUsers: 220,
        errors: 0,
        description: 'Microsoft Exchange email integration'
      },
      {
        id: 'backup',
        name: 'Backup Directory',
        type: 'LDAP',
        status: 'disconnected',
        lastSync: new Date(Date.now() - 24 * 60 * 60 * 1000),
        syncedUsers: 0,
        errors: 1,
        description: 'Secondary LDAP directory for failover'
      }
    ];

    setIntegrations(mockIntegrations);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'text-success';
      case 'warning':
        return 'text-warning';
      case 'disconnected':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return 'CheckCircle';
      case 'warning':
        return 'AlertTriangle';
      case 'disconnected':
        return 'XCircle';
      default:
        return 'Circle';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'connected':
        return 'bg-success text-success-foreground';
      case 'warning':
        return 'bg-warning text-warning-foreground';
      case 'disconnected':
        return 'bg-error text-error-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'LDAP':
        return 'Database';
      case 'SAML':
        return 'Shield';
      case 'API':
        return 'Zap';
      case 'Exchange':
        return 'Mail';
      default:
        return 'Plug';
    }
  };

  const formatLastSync = (date) => {
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      return `${days}d ago`;
    }
  };

  const handleSyncNow = (integrationId) => {
    setIntegrations(prev =>
      prev?.map(integration =>
        integration?.id === integrationId
          ? { ...integration, lastSync: new Date(), status: 'connected' }
          : integration
      )
    );
    setLastSyncTime(new Date());
    onSyncNow(integrationId);
  };

  const connectedCount = integrations?.filter(i => i?.status === 'connected')?.length;
  const warningCount = integrations?.filter(i => i?.status === 'warning')?.length;
  const disconnectedCount = integrations?.filter(i => i?.status === 'disconnected')?.length;

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Integration Status</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Monitor external system connections and data synchronization
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSyncNow('all')}
            iconName="RefreshCw"
            iconPosition="left"
          >
            Sync All
          </Button>
        </div>

        {/* Status Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-success rounded-full"></div>
            <div>
              <div className="text-lg font-semibold text-card-foreground">{connectedCount}</div>
              <div className="text-sm text-muted-foreground">Connected</div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-warning rounded-full"></div>
            <div>
              <div className="text-lg font-semibold text-card-foreground">{warningCount}</div>
              <div className="text-sm text-muted-foreground">Warning</div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-3 h-3 bg-error rounded-full"></div>
            <div>
              <div className="text-lg font-semibold text-card-foreground">{disconnectedCount}</div>
              <div className="text-sm text-muted-foreground">Disconnected</div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Icon name="Clock" size={16} className="text-muted-foreground" />
            <div>
              <div className="text-sm font-medium text-card-foreground">Last Sync</div>
              <div className="text-sm text-muted-foreground">{formatLastSync(lastSyncTime)}</div>
            </div>
          </div>
        </div>
      </div>
      {/* Integration List */}
      <div className="divide-y divide-border">
        {integrations?.map((integration) => (
          <div key={integration?.id} className="p-6 hover:bg-muted/30 transition-smooth">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                  <Icon name={getTypeIcon(integration?.type)} size={20} className="text-muted-foreground" />
                </div>
                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="font-medium text-card-foreground">{integration?.name}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(integration?.status)}`}>
                      {integration?.status?.charAt(0)?.toUpperCase() + integration?.status?.slice(1)}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{integration?.description}</p>
                  <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                    <span>Type: {integration?.type}</span>
                    <span>Users: {integration?.syncedUsers}</span>
                    {integration?.errors > 0 && (
                      <span className="text-error">Errors: {integration?.errors}</span>
                    )}
                    <span>Last sync: {formatLastSync(integration?.lastSync)}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className={getStatusColor(integration?.status)}>
                  <Icon name={getStatusIcon(integration?.status)} size={20} />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSyncNow(integration?.id)}
                  iconName="RefreshCw"
                  disabled={integration?.status === 'disconnected'}
                >
                  Sync
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onConfigureIntegration(integration?.id)}
                  iconName="Settings"
                >
                  Configure
                </Button>
              </div>
            </div>

            {/* Error Details */}
            {integration?.errors > 0 && (
              <div className="mt-4 p-3 bg-error/10 border border-error/20 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Icon name="AlertTriangle" size={16} className="text-error" />
                  <span className="text-sm font-medium text-error">Sync Issues Detected</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {integration?.errors} user{integration?.errors > 1 ? 's' : ''} failed to sync. 
                  Check the integration logs for detailed error information.
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Footer */}
      <div className="p-4 bg-muted/30 border-t border-border">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Icon name="Info" size={16} />
            <span>Automatic sync runs every 30 minutes</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onConfigureIntegration('settings')}
            iconName="Settings"
            iconPosition="left"
          >
            Sync Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default IntegrationStatusPanel;